import moment from "moment";
import { isNumber } from "util";
import flow from "lodash/fp/flow";
import isNil from "lodash/fp/isNil";
import update from "lodash/fp/update";
import isNaN from "lodash/fp/isNaN";
import pick from "lodash/fp/pick";
import capitalize from "lodash/fp/capitalize";
import assign from "lodash/fp/assign";
import map from "lodash/fp/map";
import first from "lodash/fp/first";
import find from "lodash/fp/find";
import cloneDeep from "lodash/fp/cloneDeep";
import omit from "lodash/fp/omit";
import each from "lodash/fp/each";
import flatten from "array-flatten";
import uniqBy from "lodash/fp/uniqBy";
import pickBy from "lodash/fp/pickBy";
import includes from "lodash/fp/includes";
import filter from "lodash/fp/filter";
import isObject from "lodash/fp/isObject";
import groupBy from "lodash/fp/groupBy";
import identity from "lodash/fp/identity";
import {
  toObject,
  formatDate,
  convertAmPmTimeToMinutes,
} from "../../core/utils";
import constants from "../../core/constants";
import * as fields from "./fields";

class CorporatePageTransform {
  constructor($injector, Events, Lists, $stateParams) {
    ("ngInject");
    this.$stateParams = $stateParams;
    this.loggedUser = $injector.get("Authentication").getOwner();
    this.Lists = Lists.all;
    this.weekDaysList = Lists.all.weekDays;
    this.resetPasswordEvent = Events.resetPasswordEvent("admin");
    this.editLookerEvent = Events.editLookerEvent("admin");
    this.removeAdminEvent = Events.removeAdminEvent("admin");
    this.openOfficeLocationEvent = Events.openOfficeLocation("office_location");
    this.getShiftsAddOptions = this.getShiftsAddOptions.bind(this);
    this.removeOfficeLocationEvent = Events.removeOfficeLocation(
      "office_location"
    );
    this.viewOfficeLocationEvent = Events.viewOfficeLocationEvent(
      "office_location"
    );
    this.removeShiftEvent = Events.removeShift("shift");

    this.setDefaultOfficeLocationEvent = Events.setDefaultLocation(
      "office_location"
    );

    this.changeOfficeLocationStatusEvent = Events.changeOfficeLocationStatus(
      "office_locations"
    );

    this.changeShiftStatusEvent = Events.changeShiftStatus("shift");

    // NOTE: name is used for (user roles)
    this.holidayItem = fields.holidayItem;
    this.subscriptionFields = fields.subscriptionFields;
    this.reservationFields = fields.reservationFields;
    this.invoiceFields = fields.invoiceFields;
    this.userFields = fields.userFields;

    this.corporateAdminFields = [
      { title: "Name", name: "name" },
      { title: "Email", name: "email" },
      { title: "Phone", name: "phone" },
      { title: "Created At", name: "createdAt" },
      {
        title: "Reset Password",
        name: "resetPassword",
        action: true,
        actionName: "Reset Password",
        actionIcon: "settings_backup_restore",
        onActionClick: (admin) => {
          Events.emit(this.resetPasswordEvent, admin);
        },
      },
      {
        title: "Edit Looker Roles",
        name: "editLookerRoles",
        action: true,
        actionName: "Edit Looker Roles",
        actionIcon: "edit",
        onActionClick: (admin) => {
          Events.emit(this.editLookerEvent, admin);
        },
      },
      {
        title: "Delete Admin",
        name: "deleteAdmin",
        action: true,
        actionName: "Delete Admin",
        actionIcon: "remove_circle",
        onActionClick: (admin) => {
          Events.emit(this.removeAdminEvent, admin);
        },
      },
    ];
    this.holidayFields = fields.holidayFields;
    this.holidayAddOptions = fields.holidayAddOptions;

    this.officeLocationItem = fields.officeLocationItem;
    this.officeLocationsFields = [
      { title: "Location Name", name: "name" },
      {
        title: "Location Coordinates",
        name: "coords",
        action: true,
        actionName: "Open Location",
        onActionClick: (officeLocation) => {
          Events.emit(this.openOfficeLocationEvent, officeLocation);
        },
      },
      {
        title: "Default",
        name: "default_location",
        select: true,
        onSelect: (officeLocation) => {
          Events.emit(this.setDefaultOfficeLocationEvent, officeLocation);
        },
      },
      {
        title: "Status",
        name: "is_active",
        toggle: true,
        switchOnText: "Active",
        switchOffText: "InActive",
        onToggle: (officeLocation) => {
          Events.emit(this.changeOfficeLocationStatusEvent, officeLocation);
        },
      },
      {
        title: "Delete",
        name: "delete",
        action: true,
        actionName: "Delete",
        actionIcon: "remove_circle",
        onActionClick: (officeLocation) => {
          Events.emit(this.removeOfficeLocationEvent, officeLocation);
        },
      },
      {
        title: "View",
        name: "view",
        action: true,
        actionName: "View",
        actionIcon: "visibility",
        onActionClick: (officeLocation) => {
          Events.emit(this.viewOfficeLocationEvent, officeLocation);
        },
      },
    ];

    this.officeLocationsAddOptions = fields.officeLocationsAddOptions;

    this.shiftItem = fields.shiftItem;
    this.shiftsFields = [
      { title: "Name", name: "shiftName" },
      { title: "Start Date", name: "startDate" },
      { title: "End Date", name: "endDate" },

      { title: "Direction", name: "direction" },
      { title: "To/From Office Time", name: "officeTime" },
      { title: "Office Location", name: "officeLocation" },
      { title: "Applicable Days", name: "weekdays" },
      {
        title: "Status",
        name: "is_active",
        toggle: true,
        switchOnText: "Active",
        switchOffText: "InActive",
        nullText: "Completed",
        onToggle: (shift) => {
          Events.emit(this.changeShiftStatusEvent, shift);
        },
      },
      {
        title: "Delete",
        name: "delete",
        action: true,
        actionName: "Delete",
        actionIcon: "remove_circle",
        onActionClick: (shift) => {
          Events.emit(this.removeShiftEvent, shift);
        },
      },
    ];

    this.uploadsFields = fields.uploadsFields;
    this.durations = fields.durations;
    this.planType = fields.planType;
    this.prices = fields.prices;
    this.supplyPrice = fields.supplyPrices;
    this.accountManagers = fields.accountManagers;
    // Contracts fields for stepper component
    this.contractSteps = fields.contractSteps;

    this.addOptions = fields.addOptions;
  }

  getShiftsAddOptions(corporateId) {
    return {
      title: "Add New Shift",
      icon: "add",
      fields: [
        {
          name: "start_date",
          title: "Select Start Date",
          icon: "date_range",
          date: true,
          formatter: formatDate,
          minDate: new Date(),
          isRequired: true,
        },
        {
          name: "end_date",
          title: "Select End Date",
          icon: "date_range",
          date: true,
          formatter: formatDate,
          minDate: new Date(),
          isRequired: true,
        },
        {
          name: "direction",
          title: "Shift Direction",
          isRequired: true,
          list: "officeDirection",
        },
        {
          name: "time",
          title: "To/From Office Time",
          time: true,
          formatter: convertAmPmTimeToMinutes,
          isRequired: true,
        },
        {
          name: "weekdays",
          title: "Applicable Days of the Week",
          isScheduleDays: true,
          isRequired: true,
        },
        {
          name: "office_location",
          title: "Office Location",
          resource: "Corporate",
          resourceMethod: "getAllOfficeLocations",
          resourceParams: { limit: 0, id: corporateId, is_active: true },
          listTransformer: "CorporateTransforms",
          listTransformerMethod: "justifyOfficeLocations",
          isRequired: true,
          isListResource: true,
        },
      ],
    };
  }

  getShiftsFilters(corporateId, isExport) {
    const baseFilters = [
      {
        name: "date",
        title: "Date/Date Range",
        isDate: true,
        hasRange: true,
      },
      {
        name: "time",
        title: "To/From Office Time",
        icon: "schedule",
        list: "shiftsOfficeTimesList",
        isList: true,
        resource: "Corporate",
        resourceMethod: "getShiftsOfficeTimes",
        onResourceSuccess: (hits) => {
          this.Lists.shiftsOfficeTimesList = [];
          this.Lists.shiftsOfficeTimesList = hits
            .sort((a, b) => a - b)
            .map((time) => ({
              name: time.toString(),
              title: moment()
                .startOf("day")
                .add(time, "minutes")
                .format("hh:mm A"),
              param: "time",
            }));
        },
        resourceParams: { id: corporateId },
        multiple: true,
      },
    ];
    if (!isExport) {
      baseFilters.push({
        name: "office_location",
        title: "Office Location",
        list: "officeLocationsList",
        isList: true,
        icon: "place",
        resource: "Corporate",
        resourceMethod: "getAllOfficeLocations",
        onResourceSuccess: (hits) => {
          this.Lists.officeLocationsList = [];
          this.Lists.officeLocationsList = hits.map(({ _id, name }) => ({
            name: _id,
            title: name,
            param: "office_location",
          }));
        },
        resourceParams: { id: corporateId },
        multiple: true,
      });

      baseFilters.push({
        name: "status",
        title: "Status",
        icon: "check_circle_outline",
        list: "shiftStatus",
      });
    }
    return baseFilters;
  }

  justify(data) {
    this.assignedAccountManager = this.justifyAccountManager(
      data.account_managers
    );
    const formatDate = (item) => {
      item.createdAt = moment(item.createdAt).format(constants.dateTimeFormat);
      return item;
    };

    const bulkUsersDetails = filter((detail) => detail.type === "users")(
      data.bulk_upload_details
    );
    data.bulk_users_details =
      Array.isArray(bulkUsersDetails) && bulkUsersDetails.pop();
    if (isNil(data.wht_percentage)) data.wht_percentage = null;
    if (!isNil(data.hotlines)) {
      data.hotlines = this.formatCorporateArrays(data.hotlines, "hotlines");
    }
    if (!isNil(data.account_managers)) {
      data.account_managers = this.formatCorporateArrays(
        data.account_managers,
        "account_managers"
      );
    }
    if (!isNil(data.emails)) {
      data.emails = this.formatCorporateArrays(data.emails, "emails");
    }
    if (!isNil(data.numbers)) {
      data.numbers = this.formatCorporateArrays(data.numbers, "numbers");
    }

    data.allow_skip_station_with_bookings = !isNil(
      data.allow_skip_station_with_bookings
    )
      ? data.allow_skip_station_with_bookings
      : true;

    data.allow_skip_station = !isNil(data.allow_skip_station)
      ? data.allow_skip_station
      : true;

    return toObject(flow([formatDate, identity])(data));
  }

  formatCorporateArrays(array, key) {
    const clonedArr = [];
    array.forEach((item) => {
      if (isObject(item)) {
        // Case of array of Objects.
        item.key = key;
        item.isDisabled = true;
        // To know if it is array of object or not !
        clonedArr.isObject = true;
        clonedArr.push(item);
      } else {
        // Case of array of Strings.
        const newItem = {
          key,
          isDisabled: true,
          value: item,
        };
        clonedArr.push(newItem);
      }
    });
    clonedArr.listItems = true;
    return clonedArr;
  }

  justifySubscription(data) {
    const format = (item) => {
      item = assign({ _id: item._id }, item._source);
      return item;
    };
    return map(format)(data);
  }

  justifyInvoice(data) {
    const format = (item) => {
      item = cloneDeep(item);

      switch (item.status) {
        case "in_progress":
          item.status = "In Progress";
          break;
        case "pending":
          item.status = "Pending";
          break;
        case "final":
          item.status = "Final";
          break;
        default:
          break;
      }
      item.start_date = moment
        .utc(item.start_date)
        .format(constants.dateTimeFormat);
      item.end_date = moment
        .utc(item.end_date)
        .format(constants.dateTimeFormat);
      item.total_amount /= constants.currencyBy;
      item.total_amount = `${item.total_amount} ${item.currency}`;
      return item;
    };
    return map(format)(data);
  }

  justifyHolidays(data) {
    const format = (item) => {
      item = angular.copy(item);
      item.date = moment(item.date).format(constants.dateFormat);
      if (!isNil(item.schedules)) {
        item.holidaySchedules = item.schedules.length;
      }
      item.totalSchedules = item.corporate_schedules_count;
      return item;
    };
    return map(format)(data);
  }

  justifyOfficeLocations(officeLocations) {
    const justifiedOfficeLocations = map((officeLocation) => ({
      ...officeLocation,
      name: officeLocation.name,
      coords: `${officeLocation.coords.lat},${officeLocation.coords.lng}`,
      is_active: officeLocation.is_active || false,
    }))(officeLocations);

    const defaultLocation = find((officeLocation) => officeLocation.is_default)(
      officeLocations
    );

    // Selected property in the array to control the selected default
    // office location in the whole array as we deal with radio buttons here
    justifiedOfficeLocations.selected = defaultLocation || {};

    return justifiedOfficeLocations;
  }

  justifyShifts(shifts) {
    const transformedShifts = map((shift) => {
      const shiftName = shift.name || "--";
      const startDate = moment(shift.start_date).format(constants.dateFormat);
      const endDate = moment(shift.end_date).format(constants.dateFormat);

      const officeTime = moment()
        .startOf("day")
        .add(shift.time, "minutes")
        .format("hh:mm A");
      const direction = capitalize(shift.direction.replace("_", " "));

      const weekdays = shift.weekdays.map((dayNumber) => {
        const dayObject = find((day) => day.value === dayNumber)(
          this.weekDaysList
        );
        const dayLabel = dayObject ? dayObject.shortName : "";
        return dayLabel;
      });

      return {
        _id: shift._id,
        shiftName,
        startDate,
        endDate,
        direction,
        officeTime,
        officeLocation: shift.office_location.name,
        is_active: !shift.completed ? shift.is_active : null,
        weekdays: weekdays.join(", "),
      };
    })(shifts);
    return transformedShifts;
  }

  getWhitelistedStatus(
    hasBusinessProfile,
    businessProfileCorporate,
    otherCorporate
  ) {
    if (hasBusinessProfile && businessProfileCorporate === otherCorporate) {
      return "Yes";
    } else if (
      hasBusinessProfile &&
      businessProfileCorporate !== otherCorporate
    ) {
      return "Whitelisted on another corporate";
    }
    return "No";
  }

  justifyUsers(data) {
    const format = (item) => {
      item = cloneDeep(item);
      item.createdAt = moment(item.createdAt).format(constants.dateTimeFormat);
      item.whitelistingStatus = this.getWhitelistedStatus(
        item.has_business_profile,
        item.business_profile_corporate,
        item.corporate,
        item.corpRole
      );
      return item;
    };
    return map(format)(data);
  }

  justifyReservation(data) {
    const format = (item) => {
      item = assign({ _id: item._id }, item._source);
      item.ride_price = isNumber(item.ride_price)
        ? `${item.ride_price / constants.currencyBy} ${item.route.currency}`
        : "N/A";
      item.supply_cost = isNumber(item.supply_cost)
        ? `${item.supply_cost / constants.currencyBy} ${item.route.currency}`
        : "Captain Plan";

      if (item.schedule) {
        item.schedule.name = moment()
          .startOf("day")
          .add(item.schedule.time, "ms")
          .format(constants.timeFormat);
      }
      return item;
    };
    return map(format)(data);
  }

  justifyUploads(data) {
    const format = (item) => {
      item = cloneDeep(item);
      if (!isNil(item.uploaded_file)) {
        item.uploaded_file_name = item.uploaded_file.name;
        item.uploaded_file_url = item.uploaded_file.url;
        item.uploaded_file_time = moment(item.uploaded_file.time).format(
          constants.dateTimeFormat
        );
      }
      if (!isNil(item.result_file)) {
        item.result_file_name = item.result_file.name;
        item.result_file_url = item.result_file.url;
        item.result_file_time = moment(item.result_file.time).format(
          constants.dateTimeFormat
        );
      }
      item.user = item.user.name;
      return item;
    };
    return map(format)(data);
  }

  justifyContract(data) {
    const result = {};
    const fpMap = map.convert({ cap: false });
    const formatPricings = (pricing) => {
      let result;
      const isOneDim = !isNil(pricing.ride_price) || !isNil(pricing.price);
      const updater = (n) => n * constants.currencyBy;
      const isNotNull = (item) => !(item === null);
      const isNotNaN = (item) => !isNaN(item);
      const updatePrice = (pricingInstance) => {
        let result = update("ride_price", updater, pricingInstance);
        if (!isNil(result.price)) {
          result = update("price", updater, result);
        }
        if (!isNil(result.captain_price)) {
          result = update("captain_price", updater, result);
        }
        if (
          !isNil(result.extra_ride_pricing_type) &&
          result.extra_ride_pricing_type === "fixed"
        ) {
          result = update("extra_ride_pricing_value", updater, result);
        }
        if (!isNil(result.extra_distance_price)) {
          result = update("extra_distance_price", updater, result);
        }
        result = {
          ...result,
          extra_ride_pricing: {
            type: result.extra_ride_pricing_type,
            value: result.extra_ride_pricing_value,
          },
        };
        result = pickBy(isNotNull)(result);
        result = pickBy(isNotNaN)(result);
        result = omit(["extra_ride_pricing_value", "extra_ride_pricing_type"])(
          result
        );
        return result;
      };
      if (isOneDim) {
        // One dimensional pricing for buses only contract
        result = updatePrice(pricing);
      } else {
        // Two dimensional pricing for buses/routes contract
        const pricingInstance = Object.entries(pricing);
        result = [];
        const iterateOnPrices = ([key, value]) => {
          const newPrice = updatePrice(value);
          result.push({ busTypeId: key, newPrice });
        };
        each(iterateOnPrices)(pricingInstance);
      }
      return result;
    };

    const formatSubscriptions = (item) => {
      let newSubscriptions;
      if (data.type === "busType" || data.type === "lumpsum") {
        // One dimensional pricing/buses-type
        newSubscriptions = {};
        newSubscriptions.bus_type = item._id;
        newSubscriptions.subscription_type = data.type;
        newSubscriptions.pricing = formatPricings(data.pricing[item._id]);
      } else {
        // Two dimensional pricing/bus-route-type
        newSubscriptions = [];
        const addPricing = (price) => {
          const subscriptionInstance = {};
          subscriptionInstance.bus_type = price.busTypeId;
          subscriptionInstance.subscription_type = data.type;
          subscriptionInstance.route = item._id;
          subscriptionInstance.pricing = price.newPrice;
          return subscriptionInstance;
        };
        const pricings = formatPricings(data.pricing[item._id]);
        newSubscriptions = map(addPricing)(pricings);
      }
      return newSubscriptions;
    };

    const formatPlan = (plan) => {
      const transformUnits = (item) => {
        if (item.type === "percentage") item.amount /= constants.percentage;
        else item.amount *= constants.currencyBy;

        item.threshold *= constants.fromMinToMilli;
        return item;
      };
      const updater = (type) => type.name;
      const updatedPlan = update("type", updater, plan);
      const formattedPlan = pick(["threshold", "amount", "type"], updatedPlan);
      return transformUnits(formattedPlan);
    };

    result.corporate = data.corporate;
    result.contract_type = data.type;
    const subscriptions =
      data.type === "busType" || data.type === "lumpsum"
        ? fpMap(formatSubscriptions)(data.selected_buses)
        : fpMap(formatSubscriptions)(data.selected_routes);
    if (data.type === "busType") {
      result.subscriptions = subscriptions;
    } else {
      result.subscriptions = flatten(subscriptions);
    }

    result.deduction_plan = {
      // Add new deduction types here
      ride_late_deduction: map(formatPlan)(data.deduction_plan),
    };

    result.penalty_plan = {
      // Add new penalty types here
      ride_delay_penalty: map(formatPlan)(data.penalty_plan),
    };
    if (Array.isArray(data.attachment)) result.file = first(data.attachment);
    result.start_date = moment(data.start_date).format(constants.dateFormat);
    result.expiry_date = moment(data.expiry_date).format(constants.dateFormat);
    return result;
  }

  reverseJustifyContract(data) {
    const result = {
      _id: data._id,
    };
    const getBuses = (item) => {
      return item.bus_type;
    };

    const getRoutes = (item) => {
      return item.route;
    };

    const getBasicPricing = (item) => {
      const updater = (n) => n / constants.currencyBy;
      const updatedRidePricing = update("ride_price")(updater)(item.pricing)
        .ride_price;
      const updatedCaptainPricing = update(
        "captain_price",
        updater,
        item.pricing
      ).captain_price;
      return {
        ride_price: updatedRidePricing,
        captain_price: updatedCaptainPricing || constants.NA,
      };
    };

    const getLumpsumPricing = (item) => {
      const updater = (n) => n / constants.currencyBy;
      const fixedPrice = update("price")(updater)(item.pricing).price;
      const captainPrice = update("captain_price")(updater)(item.pricing)
        .captain_price;
      let extraRidePrice;
      let extraRidePricingType;
      if (!isNil(item.pricing.extra_ride_pricing)) {
        extraRidePricingType = item.pricing.extra_ride_pricing.type;
        extraRidePrice =
          extraRidePricingType === "fixed"
            ? update("extra_ride_pricing.value")(updater)(item.pricing)
                .extra_ride_pricing.value
            : item.pricing.extra_ride_pricing.value;
      }
      const extraKMPrice = update("extra_distance_price")(updater)(item.pricing)
        .extra_distance_price;
      return {
        ...item.pricing,
        price: fixedPrice,
        captain_price: captainPrice || constants.NA,
        extra_ride_pricing_type: extraRidePricingType || constants.NA,
        extra_ride_pricing_value: extraRidePrice || constants.NA,
        extra_distance_price: extraKMPrice || constants.NA,
      };
    };

    const transformUnits = (item) => {
      if (item.type === "percentage") item.amount *= constants.percentage;
      else item.amount /= constants.currencyBy;

      item.threshold /= constants.fromMilliToMin;
      return item;
    };

    const removeDuplicates = (item) => {
      return item._id;
    };

    if (
      !isNil(data.deduction_plan) &&
      !isNil(data.deduction_plan.ride_late_deduction)
    ) {
      result.deductionPlan = map(transformUnits)(
        data.deduction_plan.ride_late_deduction
      );
    }
    if (
      !isNil(data.penalty_plan) &&
      !isNil(data.penalty_plan.ride_delay_penalty)
    ) {
      result.penaltyPlan = map(transformUnits)(
        data.penalty_plan.ride_delay_penalty
      );
    }

    const subscriptions = map((sub) => {
      const pricing =
        data.contract_type === "lumpsum"
          ? getLumpsumPricing(sub)
          : getBasicPricing(sub);
      return {
        ...sub,
        pricing,
      };
    })(data.subscriptions);

    if (data.contract_type !== "busTypeRoute") {
      result.selectedBuses = map(getBuses)(data.subscriptions);
      result.subscriptions = subscriptions;
    } else {
      result.subscriptions = groupBy((sub) => sub.route._id)(subscriptions);
      result.selectedBuses = uniqBy(removeDuplicates)(
        map(getBuses)(data.subscriptions)
      );
      result.selectedRoutes = uniqBy(removeDuplicates)(
        map(getRoutes)(data.subscriptions)
      );
    }
    result.pricing = map(
      data.contract_type === "lumpsum" ? getLumpsumPricing : getBasicPricing
    )(data.subscriptions);
    result.attachment = data.attachment;
    result.startDate = moment(data.start_date).format(constants.dateFormat);
    result.expiryDate = moment(data.expiry_date).format(constants.dateFormat);
    result.contractType = data.contract_type;
    result.version = data.version;
    result.status = data.status;
    result.rejectionReason = data.rejection_reason;
    return result;
  }

  justifyAccountManager(data) {
    const formatItem = (item) => {
      if (!isNil(item)) {
        return item._id;
      }
    };
    const accountManagers = map(formatItem)(data);
    this.assignedAccountManager = includes(this.loggedUser)(accountManagers);
    return this.assignedAccountManager;
  }
}

export default CorporatePageTransform;



// WEBPACK FOOTER //
// ./src/pages/corporate/corporate.transforms.js